import { RegisterAsIndividualIcon } from "@/assets";
import NewUsersIcon24 from "@/assets/SVG/newUsersIcon24";
import CustomizedHook from "@/components/project/autocomplatePro";
import Card from "@/pages/admin/dashboard/components/Card";
import { Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

function AreaReport({ activeTripsStats }) {
  const [tripFilterOptions, setTripFilterOptions] = useState([]);
  const [areaFilterOptions, setAreaFilterOptions] = useState([]);
  const [moduleFilterOptions, setModuleFilterOptions] = useState([]);

  const [selectedTrip, setSelectedTrip] = useState(undefined);
  const [selectedArea, setSelectedArea] = useState(undefined);
  const [selectedModule, setSelectedModule] = useState(undefined);

  useEffect(() => {
    if (!activeTripsStats?.length) {
      setTripFilterOptions([]);
      setAreaFilterOptions([]);
      setModuleFilterOptions([]);
      return;
    }

    const trips = [
      ...new Set(activeTripsStats.map((item) => item.tripName).filter(Boolean)),
    ];

    setTripFilterOptions([
      ...trips.map((e) => ({ key: e, value: e })),
      { key: "همه", value: "همه" },
    ]);

    const areas = [
      ...new Set(
        activeTripsStats
          .filter((item) => !selectedTrip || item.tripName === selectedTrip)
          .map((item) => item.name)
          .filter(Boolean),
      ),
    ];

    setAreaFilterOptions([
      ...areas.map((e) => ({ key: e, value: e })),
      { key: "همه", value: "همه" },
    ]);

    const modules = [
      ...new Set(
        activeTripsStats
          .filter(
            (item) =>
              (!selectedTrip || item.tripName === selectedTrip) &&
              (!selectedArea || item.name === selectedArea),
          )
          .flatMap(
            (item) => item.moduleStats?.map((module) => module.name) ?? [],
          )
          .filter(Boolean),
      ),
    ];

    setModuleFilterOptions([
      ...modules.map((e) => ({ key: e, value: e })),
      { key: "همه", value: "همه" },
    ]);

    if (selectedArea && !areas.includes(selectedArea)) {
      setSelectedArea("");
      setSelectedModule("");
      return;
    }

    if (selectedModule && !modules.includes(selectedModule)) {
      setSelectedModule("");
    }
  }, [activeTripsStats, selectedTrip, selectedArea]);

  const filteredTripsStats = useMemo(() => {
    return (
      activeTripsStats
        ?.filter((tripStat) => {
          const matchesTrip =
            !selectedTrip || tripStat.tripName === selectedTrip;
          const matchesArea = !selectedArea || tripStat.name === selectedArea;
          return matchesTrip && matchesArea;
        })
        .map((tripStat) => ({
          ...tripStat,
          moduleStats: selectedModule
            ? tripStat.moduleStats?.filter(
                (module) => module.name === selectedModule,
              )
            : tripStat.moduleStats,
        })) ?? []
    );
  }, [activeTripsStats, selectedTrip, selectedArea, selectedModule]);

  return (
    <>
      <Grid display="flex" flexDirection="row" gap="10px" flexWrap="wrap">
        <CustomizedHook
          parentWidth="200px"
          width={"200px"}
          placeholder={"اردو"}
          label="اردو"
          options={tripFilterOptions}
          onChange={(_, newValue) =>
            setSelectedTrip(
              newValue.value === "همه" ? undefined : newValue.value,
            )
          }
          value={selectedTrip === undefined ? "همه" : selectedTrip || null}
        />
        <CustomizedHook
          parentWidth="200px"
          width={"200px"}
          placeholder={"منطقه"}
          label="منطقه"
          options={areaFilterOptions}
          onChange={(_, newValue) =>
            setSelectedArea(
              newValue.value === "همه" ? undefined : newValue.value,
            )
          }
          value={selectedArea === undefined ? "همه" : selectedArea || null}
        />
        <CustomizedHook
          parentWidth="200px"
          width={"200px"}
          placeholder={"بخش"}
          label="بخش"
          options={moduleFilterOptions}
          onChange={(_, newValue) =>
            setSelectedModule(
              newValue.value === "همه" ? undefined : newValue.value,
            )
          }
          value={selectedModule === undefined ? "همه" : selectedModule || null}
        />
      </Grid>
      <Grid display={"flex"} flexDirection={"column"} gap={"20px"}>
        {filteredTripsStats.map((tripStat, index) => (
          <Grid
            key={index}
            display={"flex"}
            flexDirection={"column"}
            gap={"20px"}
          >
            {`${tripStat.name} - ${tripStat.city}`}
            {selectedModule === undefined && (
              <Grid display={"flex"} flexDirection={"column"} gap={"10px"}>
                <Typography
                  variant="body2"
                  color={"var(--emr-primary-color-text)"}
                >
                  آمار کلی
                </Typography>
                <Grid
                  display={"flex"}
                  flexDirection={"row"}
                  gap={"10px"}
                  flexWrap={"wrap"}
                  rowGap={"20px"}
                >
                  <Card
                    title={"کل مراجعین"}
                    icon={<RegisterAsIndividualIcon />}
                    counter={tripStat.totalPatients}
                    unit={"نفر"}
                  />
                  <Card
                    title={"کل جهادگران"}
                    icon={<RegisterAsIndividualIcon />}
                    counter={tripStat.maleMembers + tripStat.femaleMembers}
                    unit={"نفر"}
                  />
                  <Card
                    title={"کل جهادگران مرد"}
                    icon={<RegisterAsIndividualIcon />}
                    counter={tripStat.maleMembers}
                    unit={"نفر"}
                  />
                  <Card
                    title={"کل جهادگران زن"}
                    icon={<RegisterAsIndividualIcon />}
                    counter={tripStat.femaleMembers}
                    unit={"نفر"}
                  />
                </Grid>
              </Grid>
            )}
            {tripStat.moduleStats &&
              tripStat.moduleStats.map((moduleStat, index) => (
                <Grid display={"flex"} flexDirection={"column"} gap={"10px"}>
                  <Typography
                    variant="body2"
                    color={"var(--emr-primary-color-text)"}
                  >
                    {moduleStat.name}
                  </Typography>
                  <Grid
                    key={index}
                    display={"flex"}
                    flexDirection={"row"}
                    gap={"10px"}
                    flexWrap={"wrap"}
                    rowGap={"20px"}
                  >
                    <Card
                      title={"مراجعین"}
                      icon={<RegisterAsIndividualIcon />}
                      counter={moduleStat.patients}
                      unit={"نفر"}
                    />
                    <Card
                      title={"مراجعین در انتظار"}
                      icon={<NewUsersIcon24 />}
                      counter={moduleStat.waitingPatients}
                      unit={`نفر - ${((moduleStat.waitingPatients * 100) / moduleStat.patients).toFixed(1)}%`}
                    />
                    <Card
                      title={"مراجعین پذیرش شده"}
                      icon={<NewUsersIcon24 />}
                      counter={moduleStat.receptedPatients}
                      unit={`نفر - ${((moduleStat.receptedPatients * 100) / moduleStat.patients).toFixed(1)}%`}
                    />
                  </Grid>
                </Grid>
              ))}
          </Grid>
        ))}
      </Grid>
    </>
  );
}

export default AreaReport;
